import React, { useState } from 'react'
import {
  Edit, FileText, Clock, User, Wrench,
  CheckCircle, AlertCircle, Phone, Mail,
  Calendar, Euro, Package, MessageSquare,
  Download, Printer, ArrowRight, CheckSquare, MessageCircle
, Tag } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import Button from '../ui/Button'
import Badge from '../ui/Badge'
import Modal from '../Modal'
import Select from '../ui/Select'
import TextArea from '../ui/Textarea'
import Input from '../ui/Input'

import { OrdemServico, EstadoOS } from '../../types'
import { 
  getPrioridadeConfig, 
  formatValor,
  calcularIVA,
  calcularTotalComIVA
} from '../../services/ordensServico'
import ordensServicoService from '../../services/ordensServico'
import apiService from '../../services/api'
import notificationsService from '../../services/notifications'
import notificationTemplatesService from '../../services/notificationTemplates'
import { useToast } from '../Toast'
import EtiquetaModal from '../EtiquetaModal'
import settingsService from '../../services/settings'
import { DadosEtiqueta } from '../../utils/impressaoEtiqueta'
import { imprimirCupom, DadosImpressao } from '../../utils/impressaoTermica'

interface OrdemServicoDetailsProps {
  ordemServico: OrdemServico
  onEdit: () => void
  onClose: () => void
  readOnly?: boolean
}

const OrdemServicoDetails: React.FC<OrdemServicoDetailsProps> = ({
  ordemServico,
  onEdit,
  onClose,
  readOnly = false
}) => {
  const { success, error } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  
  const [showChangeStateModal, setShowChangeStateModal] = useState(false)
  const [showValorFinalModal, setShowValorFinalModal] = useState(false)
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [valorFinal, setValorFinal] = useState(ordemServico.valorFinal || 0)
  const [sendingNotification, setSendingNotification] = useState(false)
  const [newStateId, setNewStateId] = useState('')
  const [stateObservations, setStateObservations] = useState('')
  const [pendingStateChange, setPendingStateChange] = useState<{ estadoId: number, observacoes?: string } | null>(null)
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null)
  const [sendWhatsApp, setSendWhatsApp] = useState(true)
  const [sendEmail, setSendEmail] = useState(false)
  const [showEtiquetaModal, setShowEtiquetaModal] = useState(false)
  const [nomeEmpresa, setNomeEmpresa] = useState("NexaMakers OS")

  // Query para buscar dados completos da OS
  const { data: osCompleta } = useQuery({
    queryKey: ['ordem-servico', ordemServico.id],
    queryFn: () => ordensServicoService.getById(ordemServico.id),
    initialData: ordemServico
  })

  // Query para buscar estados disponíveis
  const { data: estados } = useQuery({
    queryKey: ['estados-os'],
    queryFn: () => ordensServicoService.getEstados()
  })


  // Mutation para alterar estado
  const changeStateMutation = useMutation({
    mutationFn: ({ novoEstadoId, observacoes }: { novoEstadoId: number, observacoes?: string }) => {
      return ordensServicoService.changeState(ordemServico.id, novoEstadoId, observacoes)
    },
    onSuccess: () => {
      success('Estado alterado com sucesso')
      queryClient.invalidateQueries(['ordem-servico', ordemServico.id])
      queryClient.invalidateQueries(['ordens-servico'])
      setShowChangeStateModal(false)
      setNewStateId('')
      setStateObservations('')
    },
    onError: (err: any) => {
      console.error('Erro ao alterar estado:', err)
      const message = err.response?.data?.message || err.message || 'Erro desconhecido'
      error('Erro ao alterar estado', message)
    }
  })

  // Mutation para aprovar orçamento
  const approveOrcamentoMutation = useMutation({
    mutationFn: () => ordensServicoService.approveOrcamento(ordemServico.id),
    onSuccess: () => {
      success('Orçamento aprovado com sucesso')
      queryClient.invalidateQueries(['ordem-servico', ordemServico.id])
    },
    onError: (err: any) => {
      error('Erro ao aprovar orçamento', err.message)
    }
  })

  // Mutation para atualizar valor final
  const updateValorFinalMutation = useMutation({
    mutationFn: (novoValor: number) => {
      return ordensServicoService.updateValorFinal(ordemServico.id, novoValor)
    },
    onSuccess: () => {
      success('Valor final atualizado com sucesso')
      queryClient.invalidateQueries(['ordem-servico', ordemServico.id])
      queryClient.invalidateQueries(['ordens-servico'])
      setShowValorFinalModal(false)
    },
    onError: (err: any) => {
      const message = err.response?.data?.error || err.message || 'Erro desconhecido'
      error('Erro ao atualizar valor final', message)
    }
  })

  const prioridadeConfig = getPrioridadeConfig(ordemServico.prioridade)

  // Função para determinar o tipo de PDF baseado no estado da OS
  const getPdfType = (): 'ordem' | 'fechamento' => {
    const estadoNome = ordemServico.estado.nome.toLowerCase()
    const isEstadoFinal = ordemServico.estado.estadoFinal ||
                         estadoNome.includes('entregue') ||
                         estadoNome.includes('cancelad') ||
                         estadoNome.includes('fechad') ||
                         estadoNome.includes('concluíd') ||
                         estadoNome.includes('finaliz')

    return isEstadoFinal ? 'fechamento' : 'ordem'
  }

  // Função para gerar PDF
  const handleGeneratePdf = async () => {
    try {
      const pdfType = getPdfType()
      await apiService.generateOrdemServicoPdf(ordemServico.id, pdfType)
      success(`PDF de ${pdfType === 'ordem' ? 'ordem de serviço' : 'fechamento'} gerado com sucesso`)
    } catch (err: any) {
      console.error('Erro ao gerar PDF:', err)
      const message = err.response?.data?.error || err.message || 'Erro ao gerar PDF'
      error('Erro ao gerar PDF', message)
    }
  }

  // Função para impressão térmica
  const handleThermalPrint = async () => {
    try {
      // Buscar dados da empresa
      const dadosEmpresa = await settingsService.getEmpresaForTemplates()

      // Determinar tipo de cupom baseado no estado
      const estadoNome = ordemServico.estado.nome.toLowerCase()
      let tipoCupom: 'ORDEM_SERVICO_ABERTURA' | 'ORDEM_SERVICO_FECHAMENTO' | 'ORCAMENTO' | 'RECIBO'

      if (estadoNome.includes('concluí') || estadoNome.includes('entreg') || estadoNome.includes('finaliz')) {
        tipoCupom = 'ORDEM_SERVICO_FECHAMENTO'
      } else if (estadoNome.includes('orçament')) {
        tipoCupom = 'ORCAMENTO'
      } else {
        tipoCupom = 'ORDEM_SERVICO_ABERTURA'
      }

      // Preparar dados para impressão
      const dadosImpressao: DadosImpressao = {
        id: ordemServico.id,
        tipo: tipoCupom,
        dadosEmpresa: {
          nome: dadosEmpresa.nome,
          nif: dadosEmpresa.nif,
          endereco: `${dadosEmpresa.morada}, ${dadosEmpresa.codigoPostal} ${dadosEmpresa.cidade}`,
          telefone: dadosEmpresa.telefone,
          email: dadosEmpresa.email,
          website: dadosEmpresa.website
        },
        dadosCliente: {
          nome: ordemServico.cliente.nome,
          telefone: ordemServico.cliente.telefonePrincipal || ordemServico.cliente.telefone || undefined,
          email: ordemServico.cliente.email || undefined,
          documento: ordemServico.cliente.nif || undefined
        },
        dadosEquipamento: {
          tipo: ordemServico.equipamento.modelo.tipoEquipamento.nome,
          marca: ordemServico.equipamento.modelo.marca.nome,
          modelo: ordemServico.equipamento.modelo.nome,
          numeroSerie: ordemServico.equipamento.numeroSerie || undefined,
          imei: ordemServico.equipamento.imei || undefined,
          cor: ordemServico.equipamento.cor || undefined,
          estadoFisico: ordemServico.equipamento.estadoFisico || undefined,
          observacoes: ordemServico.equipamento.observacoes || undefined,
          acessorios: ordemServico.acessorios || undefined,
          senhaDesbloqueio: ordemServico.passwordEquipamento || undefined
        },
        valorTotal: ordemServico.valorTotalComIva,
        observacoes: ordemServico.problemaRelatado,
        dataHora: new Date(ordemServico.createdAt).toLocaleString('pt-PT'),
        numeroDocumento: ordemServico.numero
      }

      // Se for fechamento ou orçamento, adicionar itens
      if (tipoCupom === 'ORDEM_SERVICO_FECHAMENTO' || tipoCupom === 'ORCAMENTO') {
        dadosImpressao.itens = ordemServico.itens?.map(item => ({
          descricao: item.produto.nome,
          quantidade: item.quantidade,
          valorUnitario: item.precoUnitario,
          valorTotal: item.subtotal
        }))
      }

      // Imprimir cupom
      await imprimirCupom(dadosImpressao)
      success('Cupom enviado para impressão')
    } catch (err: any) {
      console.error('Erro ao imprimir cupom:', err)
      error('Erro ao imprimir cupom térmico')
    }
  }

  // Função para envio por WhatsApp
  const handleSendWhatsApp = async () => {
    try {
      setSendingNotification(true)

      // Verificar se cliente tem telefone cadastrado
      if (!ordemServico.cliente.telefonePrincipal) {
        error('Cliente não possui telefone cadastrado')
        return
      }

      // Determinar tipo de evento baseado no estado atual
      let tipoEvento: 'ABERTURA' | 'MUDANCA_ESTADO' | 'APROVACAO' | 'CONCLUSAO' | 'ENTREGA' = 'MUDANCA_ESTADO'

      if (ordemServico.estado.nome.toLowerCase().includes('conclu')) {
        tipoEvento = 'CONCLUSAO'
      } else if (ordemServico.estado.nome.toLowerCase().includes('entreg')) {
        tipoEvento = 'ENTREGA'
      } else if (ordemServico.orcamentoAprovado) {
        tipoEvento = 'APROVACAO'
      }

      // Enviar notificação usando o sistema integrado
      await notificationsService.sendOrdemServicoNotification(ordemServico.id, tipoEvento)

      success('Notificação enviada por WhatsApp com sucesso')
    } catch (err: any) {
      console.error('Erro ao enviar por WhatsApp:', err)
      error(err.response?.data?.message || 'Erro ao enviar por WhatsApp')
    } finally {
      setSendingNotification(false)
    }
  }

  // Função para envio por Email
  const handleSendEmail = async () => {
    try {
      setSendingNotification(true)

      // Verificar se cliente tem email cadastrado
      if (!ordemServico.cliente.email) {
        error('Cliente não possui email cadastrado')
        return
      }

      // Determinar tipo de evento baseado no estado atual
      let tipoEvento: 'ABERTURA' | 'MUDANCA_ESTADO' | 'APROVACAO' | 'CONCLUSAO' | 'ENTREGA' = 'MUDANCA_ESTADO'

      if (ordemServico.estado.nome.toLowerCase().includes('conclu')) {
        tipoEvento = 'CONCLUSAO'
      } else if (ordemServico.estado.nome.toLowerCase().includes('entreg')) {
        tipoEvento = 'ENTREGA'
      } else if (ordemServico.orcamentoAprovado) {
        tipoEvento = 'APROVACAO'
      }

      // Enviar notificação usando o sistema integrado
      await notificationsService.sendOrdemServicoNotification(ordemServico.id, tipoEvento)

      success('Notificação enviada por email com sucesso')
    } catch (err: any) {
      console.error('Erro ao enviar por email:', err)
      error(err.response?.data?.message || 'Erro ao enviar por email')
    } finally {
      setSendingNotification(false)
    }
  }

  const handleChangeState = async () => {
    if (!newStateId) {
      error('Por favor selecione um estado')
      return
    }

    const estadoId = parseInt(newStateId)

    if (isNaN(estadoId)) {
      error('ID de estado inválido')
      return
    }

    // Buscar templates associados a este estado
    try {
      const templates = await notificationTemplatesService.getByEstado(estadoId)

      if (templates && templates.length > 0) {
        // Há templates disponíveis, mostrar modal de notificação
        setPendingStateChange({
          estadoId,
          observacoes: stateObservations || undefined
        })
        setAvailableTemplates(templates)
        setSelectedTemplate(templates[0].id) // Selecionar primeiro template por padrão
        setShowChangeStateModal(false)
        setShowNotificationModal(true)
      } else {
        // Sem templates, alterar estado diretamente
        changeStateMutation.mutate({
          novoEstadoId: estadoId,
          observacoes: stateObservations || undefined
        })
      }
    } catch (err) {
      console.error('Erro ao buscar templates:', err)
      // Em caso de erro, alterar estado sem notificação
      changeStateMutation.mutate({
        novoEstadoId: estadoId,
        observacoes: stateObservations || undefined
      })
    }
  }

  const handleConfirmStateChange = async () => {
    if (!pendingStateChange) return

    try {
      setSendingNotification(true)

      // 1. Alterar o estado da OS
      await ordensServicoService.changeState(
        ordemServico.id,
        pendingStateChange.estadoId,
        pendingStateChange.observacoes
      )

      // 2. Enviar notificações se solicitado
      if ((sendWhatsApp || sendEmail) && selectedTemplate) {
        await notificationsService.sendManualNotification({
          ordemServicoId: ordemServico.id,
          templateId: selectedTemplate,
          sendWhatsApp,
          sendEmail
        })
      }

      success('Estado alterado com sucesso' + ((sendWhatsApp || sendEmail) ? ' e notificações enviadas' : ''))

      // Atualizar queries
      queryClient.invalidateQueries(['ordem-servico', ordemServico.id])
      queryClient.invalidateQueries(['ordens-servico'])

      // Fechar modals e limpar estados
      setShowNotificationModal(false)
      setPendingStateChange(null)
      setNewStateId('')
      setStateObservations('')
      setSelectedTemplate(null)
      setSendWhatsApp(true)
      setSendEmail(false)
    } catch (err: any) {
      console.error('Erro ao alterar estado:', err)
      error('Erro ao alterar estado', err.response?.data?.message || err.message)
    } finally {
      setSendingNotification(false)
    }
  }

  const handleSkipNotification = () => {
    if (!pendingStateChange) return

    // Alterar estado sem enviar notificação
    changeStateMutation.mutate({
      novoEstadoId: pendingStateChange.estadoId,
      observacoes: pendingStateChange.observacoes
    })

    setShowNotificationModal(false)
    setPendingStateChange(null)
    setSelectedTemplate(null)
    setSendWhatsApp(true)
    setSendEmail(false)
  }

  const getStatusColor = (estado: string) => {
    const name = estado.toLowerCase()
    if (name.includes('conclu') || name.includes('entregue')) return 'green'
    if (name.includes('cancel') || name.includes('recus')) return 'red'
    if (name.includes('anda') || name.includes('repar')) return 'blue'
    if (name.includes('espera') || name.includes('pend')) return 'yellow'
    return 'gray'
  }

  // Verificar se a OS pode ser fechada
  // Estados que permitem fechamento: em andamento, aguardando, reparando, etc.
  // Estados que NÃO permitem: entregue, cancelado
  const estadoAtual = ordemServico.estado.nome.toLowerCase()

  // Verificar se a OS está arquivada (estado final)
  // Carregar nome da empresa para etiqueta
  React.useEffect(() => {
    settingsService.getEmpresaSettings().then(empresa => {
      setNomeEmpresa(empresa.nome || 'NexaMakers OS')
    }).catch(() => {})
  }, [])

  const isOSArquivada = ordemServico.estado.estadoFinal ||
                        estadoAtual.includes('entregue') ||
                        estadoAtual.includes('cancelad') ||
                        estadoAtual.includes('fechad') ||
                        estadoAtual.includes('concluíd') ||
                        estadoAtual.includes('finaliz')

  // Dados para etiqueta
  const dadosEtiqueta: DadosEtiqueta = {
    nomeEmpresa,
    numeroOS: ordemServico.numero,
    nomeCliente: ordemServico.cliente.nome,
    dataEntrada: new Date(ordemServico.dataEntrada).toLocaleDateString('pt-PT'),
    horaEntrada: new Date(ordemServico.dataEntrada).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }),
    equipamento: ordemServico.equipamento?.modelo ? `${ordemServico.equipamento.modelo.marca?.nome || ''} ${ordemServico.equipamento.modelo.nome || ''}`.trim() : undefined,
    telefone: ordemServico.cliente.telefonePrincipal || ordemServico.cliente.telefone || undefined
  }

  const canClose = !isOSArquivada && !readOnly
  const canChangeState = !isOSArquivada && !readOnly
  const canEdit = !isOSArquivada && !readOnly
  const canApproveOrcamento = !isOSArquivada && !readOnly && !ordemServico.orcamentoAprovado && ordemServico.valorOrcamento > 0

  // Se já está concluído, mostrar botão de "Entregar"
  const isCompleted = estadoAtual.includes('conclu')

  // Debug - remover depois
  console.log('Debug Fechar OS:', {
    estadoAtual,
    orcamentoAprovado: ordemServico.orcamentoAprovado,
    valorOrcamento: ordemServico.valorOrcamento,
    canClose,
    isCompleted
  })

  return (
    <div className="space-y-6">
      {/* Header com ações */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {ordemServico.numero}
            </h2>
            <p className="text-gray-600">{ordemServico.cliente.nome}</p>
          </div>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            {canClose && (
              <Button
                size="sm"
                onClick={() => navigate(`/ordens-servico/${ordemServico.id}/fechar`)}
                leftIcon={<CheckSquare className="h-4 w-4" />}
                variant={isCompleted ? "default" : "default"}
              >
                {isCompleted ? 'Registar Pagamento e Entregar' : 'Fechar OS'}
              </Button>
            )}
            {canChangeState && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChangeStateModal(true)}
                leftIcon={<ArrowRight className="h-4 w-4" />}
              >
                Alterar Estado
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              leftIcon={<Edit className="h-4 w-4" />}
              disabled={!canEdit}
            >
              Editar
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações principais */}
        <div className="lg:col-span-2 space-y-6">
          {/* Estado e Prioridade */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Estado Atual</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge
                  variant={getStatusColor(ordemServico.estado.nome) as any}
                  text={ordemServico.estado.nome}
                />
                <Badge
                  variant={prioridadeConfig.color as any}
                  text={prioridadeConfig.label}
                />
              </div>
              {ordemServico.usuarioResponsavel && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="h-4 w-4" />
                  {ordemServico.usuarioResponsavel.nome}
                </div>
              )}
            </div>
          </div>

          {/* Dados do Equipamento */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Equipamento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Tipo</label>
                <p className="text-sm text-gray-900">{ordemServico.equipamento.modelo.tipoEquipamento.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Marca</label>
                <p className="text-sm text-gray-900">{ordemServico.equipamento.modelo.marca.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Modelo</label>
                <p className="text-sm text-gray-900">{ordemServico.equipamento.modelo.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Estado Físico</label>
                <p className="text-sm text-gray-900">{ordemServico.equipamento.estadoFisico}</p>
              </div>
              {ordemServico.equipamento.numeroSerie && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Número de Série</label>
                  <p className="text-sm text-gray-900 font-mono">{ordemServico.equipamento.numeroSerie}</p>
                </div>
              )}
              {ordemServico.equipamento.imei && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">IMEI</label>
                  <p className="text-sm text-gray-900 font-mono">{ordemServico.equipamento.imei}</p>
                </div>
              )}
            </div>
          </div>

          {/* Problema e Diagnóstico */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Diagnóstico
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Problema Relatado</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap bg-white p-3 rounded border">
                  {ordemServico.problemaRelatado}
                </p>
              </div>
              {ordemServico.diagnostico && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Diagnóstico</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap bg-white p-3 rounded border">
                    {ordemServico.diagnostico}
                  </p>
                </div>
              )}
              {ordemServico.solucao && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Solução</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap bg-white p-3 rounded border">
                    {ordemServico.solucao}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Histórico de Estados */}
          {osCompleta?.historicos && osCompleta.historicos.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Histórico
              </h3>
              <div className="space-y-3">
                {osCompleta.historicos.map((historico, index) => (
                  <div key={historico.id} className="flex items-start gap-3 bg-white p-3 rounded border">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1.5"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-gray-900">
                          {historico.estadoAnterior?.nome || 'Criado'} → {historico.estadoNovo.nome}
                        </span>
                        <span className="text-gray-500">
                          por {historico.usuario.nome}
                        </span>
                        <span className="text-gray-400">
                          {new Date(historico.createdAt).toLocaleDateString('pt-PT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      {historico.observacoes && (
                        <p className="text-sm text-gray-600 mt-1">{historico.observacoes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Informações do Cliente */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Cliente</h3>
            <div className="space-y-2">
              <p className="font-medium text-gray-900">{ordemServico.cliente.nome}</p>
              {ordemServico.cliente.telefonePrincipal && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="h-4 w-4" />
                  {ordemServico.cliente.telefonePrincipal}
                </div>
              )}
              {ordemServico.cliente.email && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="h-4 w-4" />
                  {ordemServico.cliente.email}
                </div>
              )}
            </div>
          </div>

          {/* Valores */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
              <Euro className="h-4 w-4" />
              Valores
            </h3>
            <div className="space-y-3">
              {/* Estimativa Inicial */}
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Estimativa Inicial</span>
                  <span className="text-sm text-blue-700">
                    {formatValor(ordemServico.valorOrcamento)}
                  </span>
                </div>
                <p className="text-xs text-blue-600">Valor informado ao cliente na abertura da OS</p>
              </div>

              {/* Valor Real do Orçamento */}
              <div className="bg-green-50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Orçamento Final</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-700">
                      {ordemServico.valorFinal > 0 ? formatValor(ordemServico.valorFinal) : 'A definir'}
                    </span>
                    {canEdit && (
                      <button
                        onClick={() => setShowValorFinalModal(true)}
                        className="text-green-600 hover:text-green-800 p-1"
                        title="Definir valor final"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-green-600">Valor real após diagnóstico técnico</p>
              </div>

              {/* Cálculos baseados no valor final (se existir) ou estimativa */}
              {(ordemServico.valorFinal > 0 || ordemServico.valorOrcamento > 0) && (
                <>
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {ordemServico.valorFinal > 0 ? 'Valor Final' : 'Estimativa'}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatValor(ordemServico.valorFinal > 0 ? ordemServico.valorFinal : ordemServico.valorOrcamento)}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">IVA (23%)</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatValor(calcularIVA(ordemServico.valorFinal > 0 ? ordemServico.valorFinal : ordemServico.valorOrcamento))}
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900">Total com IVA</span>
                      <span className="text-lg font-bold text-gray-900">
                        {formatValor(calcularTotalComIVA(ordemServico.valorFinal > 0 ? ordemServico.valorFinal : ordemServico.valorOrcamento))}
                      </span>
                    </div>
                  </div>
                </>
              )}
              {canApproveOrcamento && (
                <div className="mt-3">
                  <Button
                    size="sm"
                    fullWidth
                    onClick={() => approveOrcamentoMutation.mutate()}
                    isLoading={approveOrcamentoMutation.isPending}
                    leftIcon={<CheckCircle className="h-4 w-4" />}
                  >
                    Aprovar Orçamento
                  </Button>
                </div>
              )}
              {ordemServico.orcamentoAprovado && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Orçamento aprovado
                </div>
              )}
            </div>
          </div>

          {/* Datas */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Datas
            </h3>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-gray-500">Entrada</span>
                <p className="text-sm text-gray-900">
                  {new Date(ordemServico.dataEntrada).toLocaleDateString('pt-PT')}
                </p>
              </div>
              {ordemServico.dataPrevistaConclusao && (
                <div>
                  <span className="text-xs text-gray-500">Previsão</span>
                  <p className="text-sm text-gray-900">
                    {new Date(ordemServico.dataPrevistaConclusao).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              )}
              {ordemServico.dataConclusao && (
                <div>
                  <span className="text-xs text-gray-500">Conclusão</span>
                  <p className="text-sm text-gray-900">
                    {new Date(ordemServico.dataConclusao).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Ações */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              {isOSArquivada ? 'Consulta' : 'Ações'}
            </h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<Tag className="h-4 w-4" />}
                onClick={() => setShowEtiquetaModal(true)}
              >
                Imprimir Etiqueta
              </Button>
            </div>
          </div>

          {/* Dados extras */}
          {(ordemServico.passwordEquipamento || ordemServico.acessorios) && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Informações Extras</h3>
              <div className="space-y-2">
                {ordemServico.passwordEquipamento && (
                  <div>
                    <span className="text-xs text-gray-500">Password</span>
                    <p className="text-sm text-gray-900 font-mono">
                      {ordemServico.passwordEquipamento}
                    </p>
                  </div>
                )}
                {ordemServico.acessorios && (
                  <div>
                    <span className="text-xs text-gray-500">Acessórios</span>
                    <p className="text-sm text-gray-900">
                      {ordemServico.acessorios}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para definir valor final */}
      <Modal
        isOpen={showValorFinalModal}
        onClose={() => setShowValorFinalModal(false)}
        title="Definir Valor Final do Orçamento"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-3 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-blue-700">Estimativa Inicial:</span>
              <span className="text-sm text-blue-600">{formatValor(ordemServico.valorOrcamento)}</span>
            </div>
          </div>

          <Input
            label="Valor Final (€)"
            type="number"
            placeholder="Digite o valor real do orçamento..."
            step="0.01"
            min="0"
            value={valorFinal}
            onChange={(e) => setValorFinal(e.target.value)}
            error={valorFinal && parseFloat(valorFinal) <= 0 ? 'Valor deve ser maior que zero' : undefined}
          />

          <div className="text-xs text-gray-500">
            Este será o valor real do orçamento após o diagnóstico técnico.
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowValorFinalModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                const valor = parseFloat(valorFinal)
                if (valor > 0) {
                  updateValorFinalMutation.mutate(valor)
                }
              }}
              isLoading={updateValorFinalMutation.isPending}
              disabled={!valorFinal || parseFloat(valorFinal) <= 0}
              leftIcon={<Edit className="h-4 w-4" />}
            >
              Definir Valor
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para alterar estado */}
      <Modal
        isOpen={showChangeStateModal}
        onClose={() => setShowChangeStateModal(false)}
        title="Alterar Estado da Ordem de Serviço"
        size="md"
      >
        <div className="space-y-4">
          <Select
            label="Novo Estado"
            value={newStateId}
            onChange={(e) => {
              const value = e.target.value
              setNewStateId(value)
            }}
            options={[
              { value: '', label: 'Selecione um estado' },
              ...(estados?.filter(e => e.id !== ordemServico.estadoId).map(estado => ({
                value: estado.id.toString(),
                label: estado.nome
              })) || [])
            ]}
          />
          
          <TextArea
            label="Observações (opcional)"
            placeholder="Digite observações sobre a mudança de estado..."
            value={stateObservations}
            onChange={(e) => setStateObservations(e.target.value)}
            rows={3}
          />

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowChangeStateModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleChangeState}
              isLoading={changeStateMutation.isPending}
              disabled={!newStateId}
              leftIcon={<ArrowRight className="h-4 w-4" />}
            >
              Alterar Estado
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de notificação ao mudar estado */}
      <Modal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        title="Enviar Notificação ao Cliente"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Existem templates de notificação associados a este estado
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  Você pode enviar uma notificação ao cliente informando sobre a mudança de estado.
                </p>
              </div>
            </div>
          </div>

          {/* Seleção de template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template de Mensagem
            </label>
            <select
              value={selectedTemplate || ''}
              onChange={(e) => setSelectedTemplate(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.nome}
                </option>
              ))}
            </select>
            {selectedTemplate && (
              <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1">Preview:</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {availableTemplates.find(t => t.id === selectedTemplate)?.template}
                </p>
              </div>
            )}
          </div>

          {/* Opções de envio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Canais de Envio
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendWhatsApp}
                  onChange={(e) => setSendWhatsApp(e.target.checked)}
                  disabled={!ordemServico.cliente.telefonePrincipal}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <MessageCircle className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">WhatsApp</p>
                  {!ordemServico.cliente.telefonePrincipal && (
                    <p className="text-xs text-red-600">Cliente não possui telefone cadastrado</p>
                  )}
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  disabled={!ordemServico.cliente.email}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Mail className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Email</p>
                  {!ordemServico.cliente.email && (
                    <p className="text-xs text-red-600">Cliente não possui email cadastrado</p>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleSkipNotification}
              disabled={sendingNotification}
            >
              Alterar sem Notificar
            </Button>
            <Button
              onClick={handleConfirmStateChange}
              isLoading={sendingNotification}
              disabled={!sendWhatsApp && !sendEmail}
              leftIcon={<MessageSquare className="h-4 w-4" />}
            >
              Alterar e Enviar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Etiqueta */}
      <EtiquetaModal
        isOpen={showEtiquetaModal}
        onClose={() => setShowEtiquetaModal(false)}
        dados={dadosEtiqueta}
      />
    </div>
  )
}

export default OrdemServicoDetails